import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/CampaignForm/SetFieldValue";
import { CampaignFormContext } from "@hardware/Widgets/CampaignTwoColumn/CampaignForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => ({
    businessEmail: state.campaignForm.fieldValues["businessEmail"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BusinessEmailStyles {
    emailTextField?: TextFieldPresentationProps;
}

export const businessEmailStyles: BusinessEmailStyles = {};

const styles = businessEmailStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const CampaignBusinessEmail: React.FC<Props> = ({ fields, businessEmail, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const campaignFormContext = React.useContext(CampaignFormContext);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState("");

    React.useEffect(() => {
        campaignFormContext.validators.businessEmail = validateBusinessEmail;
    }, [businessEmail]);

    React.useEffect(() => {
        campaignFormContext.validators.businessEmail = validateBusinessEmail;
    }, [isRequired]);

    const validateBusinessEmail = () => {
        const errorMessage =
            !businessEmail && isRequired
                ? (siteMessage("Field_Required", label) as string)
                : !businessEmail || emailRegexp.test(businessEmail)
                    ? ""
                    : (siteMessage("ContactUsForm_EmailIsInvalidErrorMessage") as string);
        setEmailErrorMessage(errorMessage);
        return !errorMessage;
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "businessEmail", value: event.target.value });
    };

    const emailBlurHandler = () => {
        validateBusinessEmail();
    };

    return (
        <TextField
            {...styles.emailTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={businessEmail || ""}
            onChange={emailChangeHandler}
            onBlur={emailBlurHandler}
            error={emailErrorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CampaignBusinessEmail),
    definition: {
        displayName: "Business Email",
        group: "Campaign" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Business Email",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "janesmith@email.com",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
